import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { Link, useLocation } from "@/router/Router";
import { useChat } from "@/state/ChatProvider";
import { useLive } from "@/state/LiveProvider";
import { Icon } from "@/components/ui/Icon";
import { AIChat } from "@/components/chat/AIChat";

/**
 * Bottom-right actions: the Miracle Assembly AI orb (always) and a Watch Live pill (only while
 * the API says the service is live, and not on the live page itself). They stack vertically so
 * they never overlap, and sit above the device's safe area. The sticky audio bar, when present,
 * raises them via the --dock-offset variable.
 */
export function FloatingActions() {
  const { open, setOpen, messages } = useChat();
  const { status } = useLive();
  const { pathname } = useLocation();
  const [hinted, setHinted] = useState(false);
  const showLive = status?.isLive && pathname !== "/watch/live";
  // The messaging screen has its own composer in that corner.
  const hidden = pathname === "/messages" || pathname.startsWith("/messages/");

  // A one-time tooltip a few seconds after arrival.
  useEffect(() => {
    const t = window.setTimeout(() => setHinted(true), 4500);
    const t2 = window.setTimeout(() => setHinted(false), 11000);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, []);

  if (hidden && !open) return <AIChat />;

  return (
    <>
      <div className={cn("fab-dock", open && "is-chat-open")}>
        {showLive ? (
          <Link to="/watch/live" className="fab-live">
            <span className="live-dot" aria-hidden />
            Watch live
          </Link>
        ) : null}
        <div className="fab-ai-wrap">
          <span className={cn("fab-ai__tip", hinted && !open && "is-shown")} aria-hidden>
            Get Details About Miracle Assembly
          </span>
          <button
            type="button"
            className={cn("fab-ai", open && "is-open")}
            aria-expanded={open}
            aria-label={open ? "Close Miracle Assembly AI" : "Ask Miracle Assembly AI"}
            onClick={() => setOpen(!open)}
            onMouseEnter={() => setHinted(false)}
          >
            <span className="fab-ai__glow" aria-hidden />
            <span className="fab-ai__icon" aria-hidden>
              <Icon name={open ? "close" : "sparkle"} size={22} />
            </span>
            <span className="fab-ai__label">Search With Miracle Assembly AI</span>
            {!open && messages.length === 0 ? <span className="fab-ai__ping" aria-hidden /> : null}
          </button>
        </div>
      </div>
      <AIChat />
    </>
  );
}
